import { apiErrorMessage } from '@agos/api-client'
import type { Product } from '@agos/api-client/model'
import { T, formatCount, tt } from '@agos/shared'
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  ConfirmDialog,
  EmptyState,
  IconButton,
  StatusBadge,
  cn,
  toast,
} from '@agos/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Edit2, FolderTree, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import type { Editing } from './ItemsPanel'
import { ReorderList } from './ReorderList'
import {
  createUpsellGroup,
  deleteUpsellGroup,
  refreshUpsellReads,
  saveUpsellGroupOrder,
  updateUpsellGroup,
  type UpsellGroup,
  type UpsellGroupPayload,
  type UpsellItem,
} from './upsell-api'
import {
  draftOfGroup,
  groupDraftProblem,
  groupLimitLabel,
  groupPayloadOf,
  groupPriceLabel,
  groupsInOrder,
  itemsOfGroup,
  linksInGroup,
  type GroupDraft,
} from './upsell-rules'

/**
 * The upsell groups - `FRONTEND.md` §9.711, owner O63/O69 with the answers #133 and #134.
 *
 * A group is a heading in the prompt with a limit (*vyberte najviac 1*, counted per unit of the product) and a
 * price (*zadarmo* at 0). **Its items are managed from the item's own editor**, opened here under this group, so
 * there is one place that writes a link rather than two that could disagree.
 */
export function GroupsPanel({
  groups,
  items,
  products,
  onEditItem,
}: {
  groups: readonly UpsellGroup[]
  items: readonly UpsellItem[]
  products: readonly Product[]
  onEditItem: (editing: Editing) => void
}) {
  const queryClient = useQueryClient()
  const ordered = groupsInOrder(groups)
  const [moved, setMoved] = useState<UpsellGroup[] | null>(null)
  const shown = moved ?? ordered
  const [editingId, setEditingId] = useState<number | 'new' | null>(null)
  const [deleting, setDeleting] = useState<UpsellGroup | null>(null)

  const nameOf = (id: number) => products.find((product) => product.id === id)?.name ?? `#${String(id)}`

  const order = useMutation({
    mutationFn: (next: UpsellGroup[]) => saveUpsellGroupOrder(next.map((group) => group.id)),
    onSuccess: async () => {
      await refreshUpsellReads(queryClient)
      setMoved(null)
    },
    onError: (caught) => {
      setMoved(null)
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Poradie skupín sa nepodarilo uložiť.')),
      })
    },
  })

  const remove = useMutation({
    mutationFn: (group: UpsellGroup) => deleteUpsellGroup(group.id),
    onSuccess: async () => {
      setDeleting(null)
      toast({ title: tt('Skupina zmazaná'), description: tt('Jej doplnky sa ponúkajú bez skupiny.') })
      await refreshUpsellReads(queryClient)
    },
    onError: (caught) =>
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Skupinu sa nepodarilo zmazať.')),
      }),
  })

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setEditingId('new')}
          disabled={editingId !== null}
          className={cn('inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm', BUTTON_PRIMARY)}
          data-upsell-group-new
        >
          <Plus className="w-4 h-4" />
          <T>Nová skupina</T>
        </button>
      </div>

      {/* Above the list rather than in its row: a row is draggable, and a text box inside a draggable element
        * starts a drag when somebody selects the text in it. */}
      {editingId !== null && (
        <GroupForm
          key={String(editingId)}
          group={editingId === 'new' ? null : (groups.find((group) => group.id === editingId) ?? null)}
          onDone={() => setEditingId(null)}
        />
      )}

      {shown.length === 0 && editingId !== 'new' ? (
        <EmptyState
          icon={<FolderTree className="w-10 h-10" />}
          title={tt('Žiadne skupiny')}
          description={tt(
            'Skupina zoradí doplnky pod jeden nadpis, obmedzí ich počet a môže im dať spoločnú cenu, napríklad omáčky zadarmo.',
          )}
        />
      ) : (
        <ReorderList
          entries={shown}
          keyOf={(group) => group.id}
          marker="upsell-group"
          disabled={order.isPending}
          onReorder={(next) => {
            setMoved(next)
            order.mutate(next)
          }}
        >
          {(group) => (
              <div className="space-y-1.5" data-upsell-group={group.id}>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-bold">
                    <T>{group.name}</T>
                  </span>
                  {!group.active && <StatusBadge color="zinc" label={tt('vypnutá')} />}
                  <span className="text-xs text-muted-foreground" data-upsell-group-limit={group.id}>
                    {groupLimitLabel(group.maxPerUnit)}
                  </span>
                  <span className="text-xs text-muted-foreground">·</span>
                  <span className="text-xs text-muted-foreground" data-upsell-group-price={group.id}>
                    {groupPriceLabel(group.priceCents)}
                  </span>
                  <span className="flex-1" />
                  <IconButton
                    label={tt('Upraviť skupinu')}
                    onClick={() => setEditingId(group.id)}
                    attributes={{ 'data-upsell-group-edit': group.id }}
                  >
                    <Edit2 className="w-3.5 h-3.5" />
                  </IconButton>
                  <IconButton
                    label={tt('Zmazať skupinu')}
                    tone="red"
                    onClick={() => setDeleting(group)}
                    attributes={{ 'data-upsell-group-delete': group.id }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </IconButton>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {itemsOfGroup(items, group.id).map((entry) => (
                    <button
                      key={entry.addonProductId}
                      type="button"
                      onClick={() => onEditItem({ addonProductId: entry.addonProductId, groupId: group.id })}
                      className="rounded-lg border border-border px-2 py-0.5 text-xs hover:border-primary/40"
                      data-upsell-group-item={entry.addonProductId}
                    >
                      <T>{nameOf(entry.addonProductId)}</T> ({formatCount(entry.products)})
                    </button>
                  ))}
                  {itemsOfGroup(items, group.id).length === 0 && (
                    <span className="text-xs text-muted-foreground">
                      <T>Zatiaľ bez doplnkov - priraďte ich v editore doplnku.</T>
                    </span>
                  )}
                </div>
              </div>
          )}
        </ReorderList>
      )}

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => deleting !== null && remove.mutate(deleting)}
        title={tt('Zmazať skupinu?')}
        description={tt(
          'Doplnky sa nezmažú: ich väzby z tejto skupiny zostanú a ponúknu sa bez skupiny, za vlastnú cenu a bez obmedzenia.',
        )}
        confirmLabel={tt('Zmazať')}
        variant="danger"
        loading={remove.isPending}
      >
        {deleting !== null && (
          <p className="text-sm" data-upsell-group-delete-count={linksInGroup(items, deleting.id)}>
            <T>Väzieb, ktoré sa presunú medzi doplnky bez skupiny</T>:{' '}
            {formatCount(linksInGroup(items, deleting.id))}
          </p>
        )}
      </ConfirmDialog>
    </div>
  )
}

/** Create or edit one group. The boxes are strings: an empty one means *none*, not nought (`groupPayloadOf`). */
function GroupForm({ group, onDone }: { group: UpsellGroup | null; onDone: () => void }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<GroupDraft>(() => draftOfGroup(group))
  const problem = groupDraftProblem(draft)
  const [tried, setTried] = useState(false)

  const save = useMutation({
    mutationFn: (payload: UpsellGroupPayload) =>
      group === null ? createUpsellGroup(payload) : updateUpsellGroup(group.id, payload),
    onSuccess: async () => {
      toast({ title: tt('Uložené'), description: draft.name.trim() })
      await refreshUpsellReads(queryClient)
      onDone()
    },
    onError: (caught) =>
      toast({
        variant: 'destructive',
        title: tt('Chyba'),
        description: apiErrorMessage(caught, tt('Skupinu sa nepodarilo uložiť.')),
      }),
  })

  const set = (patch: Partial<GroupDraft>) => setDraft((was) => ({ ...was, ...patch }))
  const inputClass = 'w-full rounded-xl border border-border bg-background px-3 py-2 text-sm'

  return (
    <form
      className="space-y-3 rounded-xl border border-primary/30 bg-primary/5 p-3"
      data-upsell-group-form={group?.id ?? 'new'}
      onSubmit={(event) => {
        event.preventDefault()
        setTried(true)
        const payload = groupPayloadOf(draft)
        if (payload !== null) save.mutate(payload)
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="space-y-1 text-sm sm:col-span-3">
          <span className="font-semibold">
            <T>Názov</T>
          </span>
          <input
            value={draft.name}
            onChange={(event) => set({ name: event.target.value })}
            className={inputClass}
            data-upsell-group-name
          />
        </label>
        <label className="space-y-1 text-sm">
          <span className="font-semibold">
            <T>Vybrať najviac</T>
          </span>
          <input
            value={draft.maxPerUnit}
            inputMode="numeric"
            placeholder={tt('bez obmedzenia')}
            onChange={(event) => set({ maxPerUnit: event.target.value })}
            className={inputClass}
            data-upsell-group-max
          />
          <span className="block text-xs text-muted-foreground">
            <T>Na jeden kus produktu. Prázdne je bez obmedzenia.</T>
          </span>
        </label>
        <label className="space-y-1 text-sm">
          <span className="font-semibold">
            <T>Cena v skupine</T>
          </span>
          <input
            value={draft.price}
            inputMode="decimal"
            placeholder={tt('vlastná cena doplnku')}
            onChange={(event) => set({ price: event.target.value })}
            className={inputClass}
            data-upsell-group-price-input
          />
          <span className="block text-xs text-muted-foreground">
            <T>Prázdne je vlastná cena doplnku, 0 je zadarmo.</T>
          </span>
        </label>
        <label className="flex items-center gap-2 self-center text-sm">
          <input
            type="checkbox"
            checked={draft.active}
            onChange={(event) => set({ active: event.target.checked })}
            className="h-4 w-4 rounded accent-primary"
            data-upsell-group-active
          />
          <T>Aktívna</T>
        </label>
      </div>
      {tried && problem !== null && (
        <p className="text-xs text-destructive" data-upsell-group-problem>
          {problem}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onDone}
          className={cn('rounded-lg px-4 py-2 text-sm', BUTTON_SECONDARY)}
          data-upsell-group-cancel
        >
          <T>Zrušiť</T>
        </button>
        <button
          type="submit"
          disabled={save.isPending}
          className={cn('rounded-lg px-4 py-2 text-sm', BUTTON_PRIMARY)}
          data-upsell-group-save
        >
          <T>Uložiť skupinu</T>
        </button>
      </div>
    </form>
  )
}
